import React, { useEffect, useMemo, useState } from "react";
import {
  ArrowDownRight,
  ArrowUpRight,
  BarChart,
  CreditCard,
  DollarSign,
  Download,
  Eye,
  FileText,
  Globe,
  LineChart,
  RefreshCw,
  ShoppingCart,
  Target,
  Users,
} from "lucide-react";
import AdminPageShell from "../components/AdminPageShell";
import API from "../../../util/API";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  LineChart as ReLineChart,
  Line,
} from "recharts";

const cx = (...arr) => arr.filter(Boolean).join(" ");

const Card = ({ children, className }) => (
  <div className={cx("bg-white border border-slate-200 rounded-2xl", className)}>{children}</div>
);

const SelectBase =
  "rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#D2042D]/30";

const PrimaryButton = ({ className, ...props }) => (
  <button
    {...props}
    className={cx(
      "inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold text-white bg-slate-900 hover:bg-[#D2042D] transition-colors",
      className
    )}
  />
);

const SecondaryButton = ({ className, ...props }) => (
  <button
    {...props}
    className={cx(
      "inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold border border-slate-200 bg-white text-slate-900 hover:bg-slate-50 transition-colors",
      className
    )}
  />
);

const toneMap = {
  slate: { iconWrap: "bg-slate-100 text-slate-700", badge: "border-slate-200 bg-slate-50 text-slate-800" },
  sky: { iconWrap: "bg-sky-100 text-sky-700", badge: "border-sky-200 bg-sky-50 text-sky-800" },
  emerald: { iconWrap: "bg-emerald-100 text-emerald-700", badge: "border-emerald-200 bg-emerald-50 text-emerald-800" },
  amber: { iconWrap: "bg-amber-100 text-amber-700", badge: "border-amber-200 bg-amber-50 text-amber-800" },
  violet: { iconWrap: "bg-violet-100 text-violet-700", badge: "border-violet-200 bg-violet-50 text-violet-800" },
  indigo: { iconWrap: "bg-indigo-100 text-indigo-700", badge: "border-indigo-200 bg-indigo-50 text-indigo-800" },
};

const Badge = ({ tone = "slate", children }) => {
  const t = toneMap[tone] ?? toneMap.slate;
  return (
    <span className={cx("inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-bold", t.badge)}>{children}</span>
  );
};

const formatShortDate = (value) => {
  if (!value) return "";
  const safe = new Date(`${value}T00:00:00`);
  if (Number.isNaN(safe.getTime())) return value;
  return safe.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

export default function AnalyticsReports() {
  const [selectedPeriod, setSelectedPeriod] = useState('30d');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [analyticsPayload, setAnalyticsPayload] = useState(null);
  const selectedPeriodLabel = useMemo(() => {
    const map = {
      "7d": "Last 7 days",
      "30d": "Last 30 days",
      "90d": "Last 90 days",
      "1y": "Last year",
    };
    return map[selectedPeriod] ?? "Custom";
  }, [selectedPeriod]);

  const periodDays = useMemo(() => {
    if (selectedPeriod === '7d') return 7;
    if (selectedPeriod === '30d') return 30;
    if (selectedPeriod === '90d') return 90;
    if (selectedPeriod === '1y') return 365;
    return 30;
  }, [selectedPeriod]);

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await API.get(`/analytics/overview?days=${periodDays}`);
        setAnalyticsPayload(res?.data?.data || null);
      } catch (e) {
        setError(e?.response?.data?.message || 'Failed to load analytics');
        setAnalyticsPayload(null);
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [periodDays]);

  const analyticsData = useMemo(() => {
    const data = analyticsPayload || {};
    const fmtMoney = (value) => {
      const n = Number(value || 0);
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
    };
    const fmtNum = (value) => new Intl.NumberFormat().format(Number(value || 0));
    const pct = (value) => `${Number(value || 0).toFixed(1)}%`;

    const key = data.keyMetrics || {};
    const changes = key.changes || {};

    return {
      keyMetrics: [
        {
          title: "Total Revenue",
          value: fmtMoney(key.revenue || 0),
          change: pct(changes.revenue),
          trend: (changes.revenue || 0) >= 0 ? "up" : "down",
          icon: DollarSign,
          tone: "emerald",
          detail: `Last ${periodDays} days`
        },
        {
          title: "Total Orders",
          value: fmtNum(key.orders || 0),
          change: pct(changes.orders),
          trend: (changes.orders || 0) >= 0 ? "up" : "down",
          icon: ShoppingCart,
          tone: "sky",
          detail: `Last ${periodDays} days`
        },
        {
          title: "Total Visitors",
          value: fmtNum(key.visitors || 0),
          change: pct(changes.visitors),
          trend: (changes.visitors || 0) >= 0 ? "up" : "down",
          icon: Eye,
          tone: "violet",
          detail: `Last ${periodDays} days`
        },
        {
          title: "Conversion Rate",
          value: pct(key.conversionRate || 0),
          change: "",
          trend: "up",
          icon: Target,
          tone: "amber",
          detail: `Last ${periodDays} days`
        },
        {
          title: "Avg Order Value",
          value: fmtMoney(key.avgOrderValue || 0),
          change: "",
          trend: "up",
          icon: CreditCard,
          tone: "emerald",
          detail: `Last ${periodDays} days`
        },
        {
          title: "Active Users",
          value: fmtNum(key.activeUsers || 0),
          change: "",
          trend: "up",
          icon: Users,
          tone: "indigo",
          detail: `Last ${periodDays} days`
        }
      ],
      salesData: {
        today: fmtMoney(data.salesData?.today || 0),
        thisWeek: fmtMoney(data.salesData?.thisWeek || 0),
        thisMonth: fmtMoney(data.salesData?.thisMonth || 0),
        growth: pct(data.salesData?.growth || 0),
        topProducts: data.salesData?.topProducts || []
      },
      trafficData: {
        totalVisitors: fmtNum(data.trafficData?.totalVisitors || 0),
        uniqueVisitors: fmtNum(data.trafficData?.uniqueVisitors || 0),
        pageViews: fmtNum(data.trafficData?.pageViews || 0),
        bounceRate: data.trafficData?.bounceRate == null ? "—" : pct(data.trafficData?.bounceRate),
        avgSessionDuration: data.trafficData?.avgSessionDuration == null ? "—" : data.trafficData?.avgSessionDuration,
        topPages: (data.trafficData?.topPages || []).map((p) => ({
          page: p.page,
          views: p.views,
          conversion: p.conversion == null ? "—" : p.conversion
        }))
      },
      geographicData: data.geographicData || [],
      reports: data.reports || [],
      unresolvedPaidOrders: Number(data.unresolvedPaidOrders || 0),
      estimatedPaidOrders: Number(data.estimatedPaidOrders || 0),
      revenueTrend: data.revenueTrend || [],
      trafficTrend: data.trafficTrend || []
    };
  }, [analyticsPayload, periodDays]);

  const revenueSeries = useMemo(() =>
    (analyticsData.revenueTrend || []).map((item) => ({
      ...item,
      label: formatShortDate(item.day)
    })),
    [analyticsData.revenueTrend]
  );

  const trafficSeries = useMemo(() =>
    (analyticsData.trafficTrend || []).map((item) => ({
      ...item,
      label: formatShortDate(item.day)
    })),
    [analyticsData.trafficTrend]
  );

  const MetricCard = ({ title, value, change, trend, icon: Icon, tone = "slate", detail }) => {
    const t = toneMap[tone] ?? toneMap.slate;
    return (
      <Card className="p-5 hover:shadow-sm transition-shadow">
        <div className="flex items-start justify-between gap-3">
          <div className={cx("p-2 rounded-2xl", t.iconWrap)}>
            <Icon className="w-5 h-5" />
          </div>
          <div
            className={cx(
              "inline-flex items-center gap-1 rounded-full border px-2 py-1 text-xs font-bold",
              trend === "up" ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-rose-200 bg-rose-50 text-rose-800"
            )}
          >
            {trend === "up" ? <ArrowUpRight className="w-3.5 h-3.5" /> : <ArrowDownRight className="w-3.5 h-3.5" />}
            <span>{change}</span>
          </div>
        </div>
        <div className="mt-4">
          <p className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">{title}</p>
          <p className="mt-2 text-3xl font-black tracking-tight text-slate-900">{value}</p>
          <p className="mt-1 text-sm text-slate-600">{detail}</p>
        </div>
      </Card>
    );
  };

  const SectionCard = ({ title, subtitle, children, className = "" }) => (
    <Card className={cx("p-5 md:p-6", className)}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="h-4 w-[3px] rounded-full bg-[#D2042D]" aria-hidden="true" />
            <h3 className="text-sm md:text-base font-black tracking-tight text-slate-900">{title}</h3>
          </div>
          <p className="mt-1 text-sm text-slate-600">{subtitle}</p>
        </div>
      </div>
      <div className="mt-4">{children}</div>
    </Card>
  );

  const reportTone = (type) => {
    if (type === "Sales") return "emerald";
    if (type === "Traffic") return "sky";
    if (type === "Engagement") return "violet";
    if (type === "Product") return "amber";
    return "slate";
  };

  const ReportItem = ({ report }) => {
    const tone = reportTone(report.type);
    const t = toneMap[tone] ?? toneMap.slate;
    return (
      <div className="flex items-center justify-between gap-4 px-4 py-3 border-b border-slate-100 hover:bg-slate-50 transition-colors">
        <div className="flex items-start gap-3 min-w-0">
          <div className={cx("p-2 rounded-2xl shrink-0", t.iconWrap)}>
            <FileText className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <p className="font-bold text-slate-900 truncate">{report.name}</p>
            <div className="mt-1 flex items-center gap-2 flex-wrap">
              <Badge tone={tone}>{report.type}</Badge>
              <span className="text-xs text-slate-500">{report.size}</span>
              <span className="text-xs text-slate-300">•</span>
              <span className="text-xs text-slate-500">{report.date}</span>
            </div>
          </div>
        </div>
        <button
          type="button"
          className="inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white p-2 text-slate-700 hover:bg-slate-50 transition-colors"
          title="Download"
        >
          <Download className="w-4 h-4" />
        </button>
      </div>
    );
  };

  return (
    <AdminPageShell
      title="Analytics & Reports"
      description="Comprehensive insights into your store's performance."
      actions={
        <>
          <select value={selectedPeriod} onChange={(e) => setSelectedPeriod(e.target.value)} className={SelectBase}>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="1y">Last year</option>
          </select>
          <PrimaryButton type="button">
            <RefreshCw className="w-4 h-4" />
            Refresh
          </PrimaryButton>
        </>
      }
    >
      <Card className="p-5 md:p-6 mb-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">Time Range</p>
            <p className="mt-2 text-2xl font-black tracking-tight text-slate-900">{selectedPeriodLabel}</p>
          </div>
          <div className="flex items-center gap-2">
            <SecondaryButton type="button">
              <Download className="w-4 h-4" />
              Export
            </SecondaryButton>
          </div>
        </div>
        {error ? (
          <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
            {error}
          </div>
        ) : null}
      </Card>

      {analyticsData.unresolvedPaidOrders > 0 ? (
        <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          {analyticsData.unresolvedPaidOrders} paid legacy order(s) are excluded from exact USD revenue because their historical exchange rate was not stored.
        </div>
      ) : null}
      {analyticsData.estimatedPaidOrders > 0 ? (
        <div className="mb-6 rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900">
          {analyticsData.estimatedPaidOrders} legacy paid order(s) use the current exchange rate for USD reporting.
        </div>
      ) : null}
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mb-6">
        {loading ? (
          <Card className="p-5">Loading metrics…</Card>
        ) : analyticsData.keyMetrics.map((metric, index) => (
          <MetricCard key={index} {...metric} />
        ))}
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <SectionCard title="Revenue Trends" subtitle="Sales performance over time">
          <div className="h-64 bg-white rounded-2xl border border-slate-200">
            {loading ? (
              <div className="h-full flex items-center justify-center text-sm text-slate-500">Loading…</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={revenueSeries} margin={{ top: 12, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="#94A3B8" />
                  <YAxis tick={{ fontSize: 11 }} stroke="#94A3B8" />
                  <Tooltip
                    formatter={(value) => [
                      new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(Number(value || 0)),
                      'Revenue'
                    ]}
                    labelFormatter={(label) => `Date: ${label}`}
                  />
                  <Area type="monotone" dataKey="revenue" stroke="#16A34A" fill="rgba(22,163,74,0.15)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </SectionCard>

        <SectionCard title="Traffic Analytics" subtitle="Visitor behavior and engagement">
          <div className="h-64 bg-white rounded-2xl border border-slate-200">
            {loading ? (
              <div className="h-full flex items-center justify-center text-sm text-slate-500">Loading…</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <ReLineChart data={trafficSeries} margin={{ top: 12, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="#94A3B8" />
                  <YAxis tick={{ fontSize: 11 }} stroke="#94A3B8" />
                  <Tooltip
                    formatter={(value, name) => [Number(value || 0).toLocaleString(), name]}
                    labelFormatter={(label) => `Date: ${label}`}
                  />
                  <Line type="monotone" dataKey="pageViews" name="Page Views" stroke="#0EA5E9" strokeWidth={2} />
                  <Line type="monotone" dataKey="uniqueVisitors" name="Unique Visitors" stroke="#D2042D" strokeWidth={2} />
                </ReLineChart>
              </ResponsiveContainer>
            )}
          </div>
        </SectionCard>
      </div>

      {/* Detailed Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <SectionCard title="Sales Overview" subtitle="Key sales metrics">
          <div className="space-y-3">
            <div className="flex justify-between items-center px-4 py-3 bg-slate-50 rounded-2xl border border-slate-200">
              <span className="text-sm font-semibold text-slate-700">Today</span>
              <span className="font-black text-slate-900">{analyticsData.salesData.today}</span>
            </div>
            <div className="flex justify-between items-center px-4 py-3 bg-slate-50 rounded-2xl border border-slate-200">
              <span className="text-sm font-semibold text-slate-700">This Week</span>
              <span className="font-black text-slate-900">{analyticsData.salesData.thisWeek}</span>
            </div>
            <div className="flex justify-between items-center px-4 py-3 bg-slate-50 rounded-2xl border border-slate-200">
              <span className="text-sm font-semibold text-slate-700">This Month</span>
              <span className="font-black text-slate-900">{analyticsData.salesData.thisMonth}</span>
            </div>
            <div className="flex justify-between items-center px-4 py-3 bg-emerald-50 rounded-2xl border border-emerald-200">
              <span className="text-sm font-semibold text-emerald-800">Growth</span>
              <span className="font-black text-emerald-800">{analyticsData.salesData.growth}</span>
            </div>
          </div>
        </SectionCard>

        <SectionCard title="Traffic Overview" subtitle="Visitor metrics">
          <div className="space-y-3">
            <div className="flex justify-between items-center px-4 py-3 bg-slate-50 rounded-2xl border border-slate-200">
              <span className="text-sm font-semibold text-slate-700">Total Visitors</span>
              <span className="font-black text-slate-900">{analyticsData.trafficData.totalVisitors}</span>
            </div>
            <div className="flex justify-between items-center px-4 py-3 bg-slate-50 rounded-2xl border border-slate-200">
              <span className="text-sm font-semibold text-slate-700">Unique Visitors</span>
              <span className="font-black text-slate-900">{analyticsData.trafficData.uniqueVisitors}</span>
            </div>
            <div className="flex justify-between items-center px-4 py-3 bg-slate-50 rounded-2xl border border-slate-200">
              <span className="text-sm font-semibold text-slate-700">Page Views</span>
              <span className="font-black text-slate-900">{analyticsData.trafficData.pageViews}</span>
            </div>
            <div className="flex justify-between items-center px-4 py-3 bg-slate-50 rounded-2xl border border-slate-200">
              <span className="text-sm font-semibold text-slate-700">Bounce Rate</span>
              <span className="font-black text-slate-900">{analyticsData.trafficData.bounceRate}</span>
            </div>
          </div>
        </SectionCard>

        <SectionCard title="Geographic Distribution" subtitle="Revenue by country">
          <div className="space-y-3">
            {analyticsData.geographicData.map((country, index) => (
              <div
                key={index}
                className="flex items-center justify-between gap-4 px-4 py-3 bg-slate-50 rounded-2xl border border-slate-200"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="p-2 rounded-2xl bg-white border border-slate-200 text-slate-500">
                    <Globe className="w-4 h-4" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-slate-900 truncate">{country.country}</p>
                    <p className="text-xs text-slate-500">{country.percentage}% share</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-black text-slate-900">${country.revenue.toLocaleString()}</p>
                  <p className="text-xs text-slate-500">{country.visitors.toLocaleString()} visitors</p>
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      </div>

      {/* Reports Section */}
      <SectionCard title="Generated Reports" subtitle="Download and manage analytics reports">
        <div className="overflow-hidden rounded-2xl border border-slate-200">
          {analyticsData.reports.map((report, index) => (
            <ReportItem key={index} report={report} />
          ))}
        </div>
        <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <button type="button" className="px-3 py-2 text-sm font-bold text-slate-600 hover:text-slate-900 transition-colors">
              Previous
            </button>
            <span className="text-sm text-slate-500">Page 1 of 1</span>
            <button type="button" className="px-3 py-2 text-sm font-bold text-slate-600 hover:text-slate-900 transition-colors">
              Next
            </button>
          </div>
          <PrimaryButton type="button">
            <Download className="w-4 h-4" />
            Export All
          </PrimaryButton>
        </div>
      </SectionCard>
    </AdminPageShell>
  );
} 
